/**
 * IconEditor — Enable/disable checkbox with icon upload + settings below
 *
 * UX pattern matches GradientEditor: single checkbox toggles the entire
 * icon feature ON/OFF, with upload field + placement + appearance shown when enabled.
 *
 * Data paths:
 * - icon: LocalizedImage object (contains translations + appearance)
 * - iconPlacement: string ('start' | 'end' | 'fixedStart' | 'fixedEnd' | 'top' | 'bottom')
 * - icon.appearance.scaleMode, dimensions.width/height, cornerRadii.uniform
 *
 * TestId convention: `style-group-icon-{control}`
 */

import React, { useCallback } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { MediaUploadField, StyledNumberInput } from '@modules/ui-components';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  fieldLabelSx,
  STYLE_FIELD_LABEL_HEIGHT,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';

const PLACEMENT_OPTIONS = [
  { value: 'start', label: 'Start (before text)' },
  { value: 'end', label: 'End (after text)' },
  { value: 'fixedStart', label: 'Fixed Start (left)' },
  { value: 'fixedEnd', label: 'Fixed End (right)' },
  { value: 'top', label: 'Top (above text)' },
  { value: 'bottom', label: 'Bottom (below)' },
];

const SCALE_MODE_OPTIONS = [
  { value: 'scaleToFill', label: 'Scale to Fill' },
  { value: 'scaleAspectFit', label: 'Aspect Fit' },
  { value: 'scaleAspectFill', label: 'Aspect Fill' },
  { value: 'center', label: 'Center' },
];

export interface IconEditorProps {
  /** Current icon value (LocalizedImage or null) */
  iconValue: unknown;
  /** Current iconPlacement value */
  iconPlacement: string | null | undefined;
  /** Icon appearance: scaleMode */
  scaleMode: string | null | undefined;
  /** Icon appearance: width */
  iconWidth: number | null | undefined;
  /** Icon appearance: height */
  iconHeight: number | null | undefined;
  /** Icon appearance: corner radius */
  cornerRadius: number | null | undefined;
  /** Callback to set the full icon LocalizedImage value */
  onIconChange: (value: unknown) => void;
  /** Callback to set iconPlacement */
  onPlacementChange: (value: string | null) => void;
  /** Callback to set icon.appearance.scaleMode */
  onScaleModeChange: (value: string | null) => void;
  /** Callback to set icon.appearance.dimensions.width */
  onWidthChange: (value: number | null) => void;
  /** Callback to set icon.appearance.dimensions.height */
  onHeightChange: (value: number | null) => void;
  /** Callback to set icon.appearance.cornerRadii.uniform */
  onCornerRadiusChange: (value: number | null) => void;
  /** Upload handler for icon files */
  onIconUpload?: (file: File) => Promise<string>;
  /** Current locale for l10n */
  locale: string;
  disabled?: boolean;
}

/**
 * Extract icon URL from LocalizedImage value.
 * LocalizedImage = { translations: { [locale]: { original, small } } }
 */
function extractIconUrl(iconValue: unknown, locale: string): string {
  if (!iconValue || typeof iconValue !== 'object') return '';
  const translations = (iconValue as Record<string, unknown>).translations as
    | Record<string, Record<string, string>>
    | undefined;
  if (!translations) return '';
  const assetRef = translations[locale];
  if (!assetRef) return '';
  return assetRef.original ?? assetRef.small ?? '';
}

export const IconEditor: React.FC<IconEditorProps> = ({
  iconValue,
  iconPlacement,
  scaleMode,
  iconWidth,
  iconHeight,
  cornerRadius,
  onIconChange,
  onPlacementChange,
  onScaleModeChange,
  onWidthChange,
  onHeightChange,
  onCornerRadiusChange,
  onIconUpload,
  locale,
  disabled = false,
}) => {
  const isEnabled = iconValue != null;
  const iconUrl = extractIconUrl(iconValue, locale);

  const handleToggle = useCallback(
    (_: unknown, checked: boolean) => {
      if (checked) {
        // Enable: create LocalizedImage with sensible appearance defaults.
        // Placement defaults to 'start' via `?? 'start'` in UI,
        // so we only need a single onChange call here.
        onIconChange({
          translations: {},
          appearance: {
            scaleMode: 'scaleAspectFit',
            dimensions: { width: 30, height: 30 },
          },
        });
      } else {
        // Disable: clear icon object only.
        // iconPlacement is a harmless orphan when icon is null.
        // Sub-paths (scaleMode, dimensions, cornerRadii) live inside icon
        // and vanish automatically when icon is set to null.
        // IMPORTANT: Only ONE onChange call per toggle — multiple calls
        // break in theme editor where handleSettingsChange clones from
        // prop (not functional updater), so second call overwrites first.
        onIconChange(null);
      }
    },
    [onIconChange],
  );

  const handleMediaChange = useCallback(
    (newValue: string | File | null) => {
      // Preserve existing appearance (scaleMode, dimensions, cornerRadii)
      // when only the image URL changes.
      const existingAppearance = iconValue && typeof iconValue === 'object'
        ? (iconValue as Record<string, unknown>).appearance
        : undefined;

      const buildIcon = (translations: Record<string, unknown>) => ({
        translations,
        ...(existingAppearance ? { appearance: existingAppearance } : {}),
      });

      if (newValue === null) {
        onIconChange(buildIcon({}));
      } else if (newValue instanceof File) {
        if (onIconUpload) {
          onIconUpload(newValue).then((url) => {
            onIconChange(buildIcon({ [locale]: { original: url, small: url } }));
          });
        }
      } else {
        if (!newValue) {
          onIconChange(buildIcon({}));
        } else {
          onIconChange(buildIcon({ [locale]: { original: newValue, small: newValue } }));
        }
      }
    },
    [onIconChange, onIconUpload, locale, iconValue],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="style-group-icon">
      {/* Enable toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, height: STYLE_FIELD_LABEL_HEIGHT, minHeight: STYLE_FIELD_LABEL_HEIGHT }}>
        <Checkbox
          size="small"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={disabled}
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          data-testid="style-group-icon-enable"
        />
        <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>
          Enable icon
        </Typography>
      </Box>

      {isEnabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Upload */}
          <MediaUploadField
            value={iconUrl || null}
            onChange={handleMediaChange}
            mediaType="image"
            showPreview={true}
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Icon image URL"
            inputTestId="style-group-icon-upload-input"
            uploadButtonTestId="style-group-icon-upload-btn"
            previewTestId="style-group-icon-upload-preview"
          />

          {/* Placement */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 60 }}>
              Placement
            </Typography>
            <Select
              size="small"
              value={iconPlacement ?? 'start'}
              onChange={(e) => onPlacementChange(e.target.value as string)}
              disabled={disabled}
              data-testid="style-group-icon-placement"
              sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
            >
              {PLACEMENT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Appearance: Scale Mode */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 60 }}>
              Scale
            </Typography>
            <Select
              size="small"
              value={scaleMode ?? 'scaleAspectFit'}
              onChange={(e) => onScaleModeChange(e.target.value as string)}
              disabled={disabled}
              data-testid="style-group-icon-scale-mode"
              sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
            >
              {SCALE_MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Dimensions + Corner Radius */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Width
              </Typography>
              <StyledNumberInput
                value={iconWidth ?? null}
                onChange={onWidthChange}
                min={0}
                max={200}
                disabled={disabled}
                data-testid="style-group-icon-width"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Height
              </Typography>
              <StyledNumberInput
                value={iconHeight ?? null}
                onChange={onHeightChange}
                min={0}
                max={200}
                disabled={disabled}
                data-testid="style-group-icon-height"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Radius
              </Typography>
              <StyledNumberInput
                value={cornerRadius ?? null}
                onChange={onCornerRadiusChange}
                min={0}
                max={100}
                disabled={disabled}
                data-testid="style-group-icon-corner-radius"
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
